import { DecimalPipe, NgClass, NgFor, NgIf } from '@angular/common';
import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { AccountService } from '../../../../Services/Accounts/account.service';
import { FirmService } from '../../../../Services/firm.service';
import { FirmSelectionService } from '../../../../Services/firm-selection.service';
import { Subscription } from 'rxjs';
import * as XLSX from 'xlsx';
import FileSaver from 'file-saver';

interface BalanceItem {
  name: string;
  amount: number;
  type: 'DR' | 'CR';
}

@Component({
  selector: 'app-balance-sheet-layout',
  imports: [NgFor, NgIf, FormsModule],
  templateUrl: './balance-sheet-layout.component.html',
  styleUrl: './balance-sheet-layout.component.css'
})
export class BalanceSheetLayoutComponent implements OnInit, OnDestroy {
  firms: any[] = [];
  selectedFirmId: any = '';
  selectedFirmName: string = 'All Firms';
  selectedYear: string = '';

  liabilities: BalanceItem[] = [];
  assets: BalanceItem[] = [];
  totalLiabilities: number = 0;
  totalAssets: number = 0;
  difference: number = 0;

  // New properties for balancing
  diffLiabilities: number = 0;
  diffAssets: number = 0;
  grandTotal: number = 0;

  // Split into columns for UI layout
  liabilitiesCol1: BalanceItem[] = [];
  liabilitiesCol2: BalanceItem[] = [];
  assetsCol1: BalanceItem[] = [];
  assetsCol2: BalanceItem[] = [];

  years: string[] = [];

  private firmSubscription: Subscription | null = null;

  constructor(
    private accountService: AccountService,
    private firmService: FirmService,
    private firmSelectionService: FirmSelectionService
  ) {
    this.generateYears();
  }

  generateYears() {
    const today = new Date();
    const currentMonth = today.getMonth(); // 0-indexed: 0=Jan, 3=April
    const currentYear = today.getFullYear();

    // In India, Financial Year starts from April (Month index 3)
    let runningStartYear = currentMonth < 3 ? currentYear - 1 : currentYear;

    this.years = [];
    // Start from 2000 up to the running FY (inclusive)
    for (let sy = 2000; sy <= runningStartYear; sy++) {
      const ey = (sy + 1).toString().slice(-2);
      const yearRange = `${sy}-${ey}`;
      this.years.push(yearRange);
    }
    this.years.reverse();
    if (this.years.length > 0) this.selectedYear = this.years[0];
  }

  ngOnInit() {
    this.loadFirms();

    // Subscribe to global firm selection
    this.firmSubscription = this.firmSelectionService.selectedFirmName$.subscribe(firm => {
      if (firm && !this.selectedFirmId) {
        this.selectedFirmId = firm.id;
        this.loadBalanceSheet();
      }
    });

    // Fallback: use localStorage
    const storedFirmId = localStorage.getItem('firm_id');
    if (storedFirmId && !this.selectedFirmId) {
      this.selectedFirmId = Number(storedFirmId);
      this.loadBalanceSheet();
    }
  }

  ngOnDestroy(): void {
    if (this.firmSubscription) {
      this.firmSubscription.unsubscribe();
    }
  }

  loadFirms() {
    this.firmService.getFirms().subscribe({
      next: (res: any) => {
        this.firms = res.data || [];
        if (this.firms.length > 0 && !this.selectedFirmId) {
          this.selectedFirmId = this.firms[0].id;
          this.setFirmName();
          this.loadBalanceSheet();
        } else if (this.selectedFirmId) {
          this.setFirmName();
          this.loadBalanceSheet();
        }
      },
      error: (err) => console.error('Error loading firms', err),
    });
  }

  loadBalanceSheet() {
    const params: any = {};
    if (this.selectedFirmId) {
      params.firm_id = this.selectedFirmId;
    }

    if (this.selectedYear) {
      // Split year like "2024-2025" to get the end of the FY (March 31, 2025)
      const parts = this.selectedYear.split('-');
      if (parts.length === 2) {
        const endYear = 2000 + parseInt(parts[1]);
        params.to_date = `${endYear}-03-31`;
      }
    }

    this.accountService.getBalanceSheet(params).subscribe({
      next: (res: any) => {
        this.liabilities = res.data.liabilities || [];
        this.assets = res.data.assets || [];
        this.totalLiabilities = res.data.total_liabilities || 0;
        this.totalAssets = res.data.total_assets || 0;
        this.difference = res.data.difference || 0;

        // Calculate dynamic balancing
        if (this.difference > 0) {
          // Assets > Liabilities
          this.diffLiabilities = this.difference;
          this.diffAssets = 0;
          this.grandTotal = this.totalAssets;
        } else if (this.difference < 0) {
          // Liabilities > Assets
          this.diffLiabilities = 0;
          this.diffAssets = Math.abs(this.difference);
          this.grandTotal = this.totalLiabilities;
        } else {
          this.diffLiabilities = 0;
          this.diffAssets = 0;
          this.grandTotal = this.totalAssets;
        }

        this.splitColumns();
      },
      error: (err) => console.error('Error loading balance sheet', err),
    });
  }

  splitColumns() {
    // Split into 2 columns for the UI grid
    const midL = Math.ceil(this.liabilities.length / 2);
    this.liabilitiesCol1 = this.liabilities.slice(0, midL);
    this.liabilitiesCol2 = this.liabilities.slice(midL);

    const midA = Math.ceil(this.assets.length / 2);
    this.assetsCol1 = this.assets.slice(0, midA);
    this.assetsCol2 = this.assets.slice(midA);
  }

  onFilterChange() {
    this.setFirmName();
    this.loadBalanceSheet();
  }

  setFirmName() {
    if (this.selectedFirmId) {
      const firm = this.firms.find(f => f.id == this.selectedFirmId);
      this.selectedFirmName = firm ? firm.name : 'Unknown Firm';
    } else {
      this.selectedFirmName = 'All Firms';
    }
  }

  formatAmount(amount: number): string {
    if (!amount && amount !== 0) return '0.00';
    return amount.toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  }

  printBalanceSheet() {
    const generatedOn = new Date().toLocaleString('en-GB');
    const fyLabel = this.selectedYear || 'Current Year';

    let printContent = `
    <html>
    <head>
      <title>Balance Sheet - ${this.selectedFirmName}</title>
      <style>
        body { font-family: Arial, sans-serif; margin: 20px; font-size: 11px; color: #333; }
        .header { text-align: center; margin-bottom: 20px; border-bottom: 2px solid #0D5BAB; padding-bottom: 10px; }
        .header h2 { margin: 0; color: #0D5BAB; text-transform: uppercase; font-size: 18px; }
        .header p { margin: 5px 0 0 0; font-weight: bold; font-size: 14px; }
        .info-grid { display: flex; justify-content: space-between; margin-bottom: 15px; border-bottom: 1px solid #ddd; padding-bottom: 5px; }
        .info-item { font-weight: bold; }
        .info-item span { font-weight: normal; }
        
        .main-container { display: flex; gap: 20px; align-items: stretch; }
        .side-panel { flex: 1; display: flex; flex-direction: column; border: 1px solid #ededed; border-radius: 8px; overflow: hidden; }
        
        .side-header { background-color: #0D5BAB; color: white; text-align: center; padding: 8px; font-weight: bold; font-size: 13px; text-transform: uppercase; }
        .assets-header { background-color: #6EB67B; }
        
        table { width: 100%; border-collapse: collapse; flex: 1; }
        th, td { padding: 6px 8px; text-align: left; border-bottom: 1px solid #f9f9f9; }
        .text-right { text-align: right; }
        
        .diff-row { background-color: #f8f9fa; font-style: italic; font-weight: bold; }
        .total-row { background-color: #0D5BAB; color: white; font-weight: bold; font-size: 13px; }
        .assets-total { background-color: #6EB67B; }
        
        @media print {
          @page { size: auto; margin: 10mm; }
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        }
      </style>
    </head>
    <body>
      <div class="header">
        <h2>Financial Balance Sheet</h2>
        <p>${this.selectedFirmName}</p>
      </div>

      <div class="info-grid">
        <div class="info-item">Financial Year: <span>${fyLabel}</span></div>
        <div class="info-item">As of Date: <span>31st March ${2000 + parseInt(this.selectedYear.split('-')[1])}</span></div>
        <div class="info-item">Generated: <span>${generatedOn}</span></div>
      </div>

      <div class="main-container">
        <!-- Liabilities Section -->
        <div class="side-panel">
          <div class="side-header">Liabilities</div>
          <table>
            <tbody>
              ${this.liabilities.map(item => `
                <tr>
                  <td>${item.name}</td>
                  <td class="text-right">${this.formatAmount(item.amount)}</td>
                </tr>
              `).join('')}
              ${this.diffLiabilities > 0 ? `
                <tr class="diff-row">
                  <td>Diff. in Balance</td>
                  <td class="text-right">${this.formatAmount(this.diffLiabilities)}</td>
                </tr>
              ` : ''}
              <tr class="blank-row"><td colspan="2">&nbsp;</td></tr>
            </tbody>
          </table>
          <div class="total-row p-2 flex justify-between px-2" style="padding: 8px; display: flex; justify-content: space-between;">
            <span>TOTAL</span>
            <span>${this.formatAmount(this.grandTotal)}</span>
          </div>
        </div>

        <!-- Assets Section -->
        <div class="side-panel" style="border-color: #6EB67B;">
          <div class="side-header assets-header">Assets</div>
          <table>
            <tbody>
              ${this.assets.map(item => `
                <tr>
                  <td>${item.name}</td>
                  <td class="text-right">${this.formatAmount(item.amount)}</td>
                </tr>
              `).join('')}
              ${this.diffAssets > 0 ? `
                <tr class="diff-row">
                  <td>Diff. in Balance</td>
                  <td class="text-right">${this.formatAmount(this.diffAssets)}</td>
                </tr>
              ` : ''}
              <tr class="blank-row"><td colspan="2">&nbsp;</td></tr>
            </tbody>
          </table>
          <div class="total-row assets-total p-2 flex justify-between px-2" style="padding: 8px; display: flex; justify-content: space-between;">
            <span>TOTAL</span>
            <span>${this.formatAmount(this.grandTotal)}</span>
          </div>
        </div>
      </div>

      <script>
        window.onload = function() {
          window.print();
          window.onafterprint = function() { window.close(); };
        }
      </script>
    </body>
    </html>
    `;

    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.open();
      printWindow.document.write(printContent);
      printWindow.document.close();
    }
  }

  exportToExcel() {
    if (this.liabilities.length === 0 && this.assets.length === 0) {
      alert('No data available to export');
      return;
    }

    // 1. Prepare Header Info
    const reportFY = this.selectedYear || 'N/A';
    const generatedOn = new Date().toLocaleString('en-GB');

    // 2. Prepare data in a side-by-side format
    const dataToExport: any[] = [];
    const maxLen = Math.max(this.liabilities.length, this.assets.length);

    for (let i = 0; i < maxLen; i++) {
      const lib = this.liabilities[i];
      const ast = this.assets[i];

      dataToExport.push({
        'Liabilities': lib ? lib.name : '',
        'Amount (Liabilities)': lib ? parseFloat(lib.amount.toString()) : 0,
        'Assets': ast ? ast.name : '',
        'Amount (Assets)': ast ? parseFloat(ast.amount.toString()) : 0
      });
    }

    // 3. Initialize Worksheet
    const worksheet = XLSX.utils.json_to_sheet([]);

    // 4. Add Professional Header Rows (Rows 1-5)
    XLSX.utils.sheet_add_aoa(worksheet, [
      [`${this.selectedFirmName.toUpperCase()} - BALANCE SHEET`],
      [`FINANCIAL YEAR: ${reportFY}`],
      [`TOTAL CONSOLIDATED ASSETS/LIABILITIES: ₹ ${this.grandTotal.toFixed(2)}`],
      [`GENERATED ON: ${generatedOn}`],
      [] // Spacer
    ], { origin: "A1" });

    // 5. Add Table Data (Starting from Row 6)
    XLSX.utils.sheet_add_json(worksheet, dataToExport, { origin: "A6", skipHeader: false });

    // 6. Add Difference and Totals
    const lastDataRow = 6 + dataToExport.length;
    XLSX.utils.sheet_add_aoa(worksheet, [
      [
        this.diffLiabilities > 0 ? 'Diff. in Balance' : '',
        this.diffLiabilities > 0 ? parseFloat(this.diffLiabilities.toString()) : '',
        this.diffAssets > 0 ? 'Diff. in Balance' : '',
        this.diffAssets > 0 ? parseFloat(this.diffAssets.toString()) : ''
      ],
      [
        'TOTAL',
        parseFloat(this.grandTotal.toString()),
        'TOTAL',
        parseFloat(this.grandTotal.toString())
      ]
    ], { origin: `A${lastDataRow + 1}` });

    // 7. Set column widths
    worksheet['!cols'] = [
      { wch: 35 }, // Liabilities
      { wch: 20 }, // Amount
      { wch: 35 }, // Assets
      { wch: 20 }, // Amount
    ];

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Balance Sheet');

    const fileName = `Balance_Sheet_${this.selectedFirmName.replace(/\s+/g, '_')}_${reportFY}.xlsx`;
    XLSX.writeFile(workbook, fileName);
  }
}
